import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const DICT = {
  en: {
    brand_tag: "Curate with clarity",
    nav_login: "Studio login",
    nav_client: "Client login",
    hero_eyebrow: "For photo studios & freelance photographers",
    hero_title_a: "Photo selection,",
    hero_title_b: "finally effortless.",
    hero_body:
      "Send one private link. Clients swipe, zoom, choose their edits, prints and frames — you receive a priced summary, an invoice and a tracked order.",
    hero_cta: "Start free trial",
    how_it_runs: "How it runs",
    pricing: "Subscription plans",
    pricing_body: "Start with a 7-day free trial. Pay with QRIS, virtual account, e-wallet or bank transfer.",
    per_month: "per month",
    per_year: "per year",
    choose_plan: "Choose plan",
    footer_note: "Client selection, curated.",
    nav_about: "About us",
    nav_contact: "Contact",
    nav_privacy: "Privacy policy",
    nav_terms: "Terms of service",
    back_home: "Back to home",
    last_updated: "Last updated",
    contact_title: "Talk to a human in Medan.",
    contact_intro:
      "Questions about pricing, a gallery that is misbehaving, or a partnership idea — send it over. We read every message ourselves.",
    support_email: "Support email",
    email_note: "Best for detailed questions, billing records and anything with screenshots attached.",
    whatsapp: "WhatsApp",
    whatsapp_note: "Fastest during a shoot day. Message us and we will reply as soon as we are off set.",
    hq_address: "Head office",
    address_note: "Visits by appointment — message first so someone is there to meet you.",
    office_hours: "Office hours",
    hours_value: "MON–SAT · 09:00–18:00 WIB",
    hours_note: "Emails sent outside these hours are answered the next working morning.",
    send_message: "Send message",
    form_heading: "Write to us directly",
    subject: "Subject",
    message: "Message",
    message_placeholder: "Tell us what you need. The more detail, the faster we can help.",
    subject_sales: "Sales & pricing",
    subject_support: "Technical support",
    subject_billing: "Billing & payments",
    subject_partnership: "Partnership / affiliate",
    subject_other: "Something else",
    contact_sent: "Message sent",
    contact_sent_body: "Thank you — your message is with us. We usually reply within one working day.",
    contact_failed: "Could not send your message. Please try again.",
    form_privacy_note:
      "We use your details only to reply to this enquiry. Nothing is added to a marketing list.",
    err_name: "Please enter your name (at least 2 characters).",
    err_email: "Please enter a valid email address.",
    err_message: "Please write at least 10 characters so we can help properly.",
    print_sizes_hint: "Ticked sizes are what your client can choose from. Prices come from your master catalogue in Settings.",
    no_catalog_sizes: "No print sizes in your catalogue yet — add them under Settings → Pricing first.",
    n_selected: "{n} selected",
    select_all: "Select all",
    clear: "Clear",
    package_price: "Package price",
    package_label: "Package name on invoice",
    package_label_placeholder: "Combo 7 Edit",
    package_price_hint: "The agreed package fee. Applied to the project invoice when this template is used.",
    discount_label: "Discount label",
    add_discount_line: "Add another discount",
    select_all_remaining: "Select all (fills package)",
    unselect_all: "Unselect all",
    bulk_selected: "{n} photos selected",
    print_qty: "Copies",
    mandatory_prints_hint: "Click a size from your price list to include it in the package, then set how many copies are covered.",
    discount_amount: "Discount amount",
    discount_placeholder: "Loyalty discount",
    add_discount: "+ Add discount",
    edit_discount: "Edit discount",
    apply_discount: "Apply discount",
    discount_saved: "Discount applied",
    admin_override_hint: "You can change sizes, frame codes, add or remove prints at any time.",
    print_added: "Print added",
    print_removed: "Print removed",
    remove_print: "Remove print",
    tsf_eyebrow: "The client journey",    tsf_title: "A streamlined 3-step selection process.",
    tsf_body:
      "We cut the client's journey down to three screens. Fewer decisions, no scrolling back and forth, and a submitted selection in minutes rather than days of WhatsApp.",
    tsf_step: "Step",
    tsf_s1_t: "Choose the edits",
    tsf_s1_b:
      "One grid, one tap per photograph. The included quota counts down in view, and any extra is priced on the spot before it is confirmed.",
    tsf_s2_t: "Add the notes",
    tsf_s2_b:
      "Revision requests attach to the photograph they belong to — brighten this one, remove that reflection — so your editor never guesses.",
    tsf_s3_t: "Route the prints",
    tsf_s3_b:
      "Pre-paid package allocations decrement live. When a size runs out, extra prints unlock at the price you configured.",

    login_title: "Welcome back.",
    signup_title: "Set up your studio.",
    studio_access: "Studio access",
    new_studio: "New studio",
    continue_google: "Continue with Google",
    or_email: "or email",
    studio_name: "Studio name",
    your_name: "Your name",
    email: "Email",
    password: "Password",
    enter_studio: "Enter studio",
    create_studio: "Create studio",
    to_signup: "No account yet? Create your studio",
    to_login: "Already have a studio? Sign in",
    client_login_title: "Track & select.",
    access_code: "Access code",
    client_signin: "Open my gallery",
    client_login_hint: "Use the email and access code your photographer sent you.",

    dashboard: "Dashboard",
    projects: "Projects",
    new_project: "New project",
    analytics: "Analytics",
    settings: "Settings",
    billing: "Billing",
    platform: "Platform",
    logout: "Log out",
    no_projects: "No galleries yet",
    no_projects_body: "Create your first project and share the private link with your client.",
    photos: "Photos",
    edits: "Edits",
    prints: "Prints",
    extra: "Extra",
    manage: "Manage",
    awaiting_client: "Awaiting client",
    submitted: "Submitted",
    project_code: "Project ID",
    project_title: "Project title",
    client_name: "Client name",
    client_email: "Client email",
    drive_link: "Google Drive folder link (shared publicly)",
    demo_photos: "Use demo photos instead (for testing the flow)",
    edit_quota: "Photos included for editing",
    allow_extra: "Allow extra edit requests",
    max_extra: "Max extra edits",
    print_quotas: "Required prints (size × quantity)",
    add_size: "Add size",
    print_sizes: "Print sizes offered",
    create_gallery: "Create gallery",
    save_changes: "Save changes",
    client_link: "Private client link",
    copy: "Copy",
    preview: "Preview",
    resync: "Re-sync Drive folder",
    download_pdf: "Download PDF",
    email_summary: "Email summary",
    reopen: "Reopen for client",
    order_status: "Order status",
    custom_charges: "Custom charges & add-ons",
    charge_label: "Description",
    amount: "Amount (IDR)",
    add_charge: "Add charge",
    invoice: "Invoice",
    total: "Total",
    client_selection: "Client selection",
    access_code_label: "Client access code",
    reset_code: "Reset code",

    step_select: "Select",
    step_notes: "Notes",
    notes_title: "Anything we should change?",
    notes_body:
      "Attach a note to any photograph — brighten it, crop it tighter, remove a reflection. Your editor sees the note against the exact image.",
    general_note_placeholder: "Anything about the order as a whole?",
    quota_reached_toast: "Base quota of {n} reached. Additional selections are charged at {price} per photo.",
    extra_added_toast: "Extra edit added · {price}",
    quota_full_no_extra: "Your package includes {n} photographs and extra edits are not enabled.",
    extra_cap_reached: "You may request at most {n} extra edits.",
    quota_left: "{n} of your package left",
    quota_used_up: "Package quota used up",
    then_each: "then {price} each",
    max_total: "max {n} total",
    n_left: "{n} left",
    quota_spent: "used up",
    price_on_request: "studio will quote",
    package_presets_hint: "Tap a price you use often, or type a custom one. It appears as the package line on the invoice.",
    unpriced_sizes_title: "These sizes have no price in your catalogue:",
    unpriced_sizes_body:
      "Clients can still request them, but extra prints of these sizes will not be charged automatically. Add them under Settings → Pricing, or untick them here.",
    step_review: "Review",
    step_extra: "Extra edits",
    step_prints: "Prints",
    step_confirm: "Confirm",
    choose_edits: "Choose your photos",
    choose_edits_body: "Select {n} photographs for editing. Tap a photo to open it full screen, then tap it to select.",
    gallery_preparing: "Your photographer is still preparing this gallery.",
    note_placeholder: "Brighten shadows, remove the sign on the left...",
    avg_per_project: "Avg / project",
    review_notes: "Add your notes",
    review_notes_body: "Leave instructions for the retoucher on each selected photograph.",
    extra_title: "Need more edits?",
    extra_body: "Add extra photographs beyond your package at {price} each.",
    prints_title: "Prints & frames",
    prints_body: "Choose sizes and frame codes. Required prints are included in your package.",
    confirm_title: "Final review",
    confirm_body: "Check everything, add a last note, then submit.",
    selected: "selected",
    included: "Included",
    extra_charge: "Extra",
    running_total: "Estimated extra charges",
    frame_code: "Frame code",
    notes: "Notes",
    add_print: "Add print",
    printing: "Printing",
    save: "Save",
    continue: "Continue",
    back: "Back",
    submit_selection: "Submit selection",
    submitted_ok: "Selection submitted",
    general_note: "Anything else for your photographer?",
    zoom_hint: "Tap the photo to zoom",
    skip: "Skip",
    keep: "Keep",
    undo: "Undo",
    deck_done: "You've been through every photo.",

    summary: "Selection summary",
    tracking: "Order tracking",
    milestone_selection_received: "Selection received",
    milestone_editing_complete: "Editing complete",
    milestone_printing_complete: "Printing complete",
    milestone_delivered: "Delivered",
    print_page: "Print this page",
    my_projects: "My galleries",
    open_gallery: "Open gallery",
    view_summary: "View summary",

    branding: "Studio branding",
    logo_url: "Logo URL",
    pricing_defaults: "Default pricing",
    extra_edit_price: "Price per extra edited photo",
    frame_price: "Price per frame",
    price_per_size: "Price per print size",
    staff: "Staff accounts",
    add_staff: "Add staff",
    role: "Role",
    remove: "Remove",
    cancel: "Cancel",
    edit: "Edit",
    revenue: "Revenue",
    revenue_hidden: "Financial data is visible to Owners only",
    period_week: "This week",
    period_month: "This month",
    period_year: "This year",
    period_custom: "Custom range",
    digital_only: "Digital-only edits",
    for_print: "Edits for print",
    frames: "Frames",
    pipeline: "Pipeline",
    plans: "Subscription plans",
    tenants: "Studios",
    plan_name: "Plan name",
    price: "Price (IDR)",
    price_usd: "Price (USD)",
    pay_with: "Pay with",
    pay_local: "Indonesia · QRIS, VA, e-wallet",
    pay_paypal: "International · PayPal (USD)",
    paypal_note: "For studios outside Indonesia. Charged once in USD — no automatic recurring billing.",
    paypal_no_usd_price: "No USD price set for this plan yet.",
    paypal_not_confirmed: "PayPal did not confirm this payment",
    paypal_pending_review: "PayPal is reviewing this payment",
    paypal_pending_review_hint: "PayPal has taken the payment but is holding it for review — usually a few hours. Your plan activates by itself as soon as the funds clear, and this page will show it. Nothing more to do.",
    order_status_pending_review: "In review",
    order_status_expired: "Expired",
    order_status_pending: "Awaiting payment",
    order_status_declined: "Declined",
    order_status_refunded: "Refunded",
    order_status_reversed: "Reversed",
    order_status_paid: "Paid",
    payment_received: "Payment received",
    payment_failed: "Payment failed",
    checkout_closed: "Checkout closed",
    interval_months: "Interval (months)",
    save_plan: "Save plan",
    current_plan: "Current plan",
    trial_until: "Trial until",
    pay_now: "Pay now",
    payment_history: "Payment history",
    apply: "Apply range",
    saved_ok: "Changes saved",
    support: "Seleksa support",
    support_body: "Questions about your studio account, billing or a project? Chat with our team on WhatsApp.",
    chat_whatsapp: "Chat on WhatsApp",
    support_prefill: "Hi Seleksa support, I need help with ",
    ob_eyebrow: "How to choose",
    ob_title: "Three gestures, that's all",
    ob_tap_open: "Tap any photo in the grid to open it full screen.",
    ob_swipe: "Swipe left or right (or use the arrows) to move between photos.",
    ob_tap_select: "Tap the photo — or the checkmark — to select or deselect it.",
    ob_pinch: "Pinch with two fingers to zoom in on the fine details.",
    ob_start: "Start choosing",
    close: "Close",
    select_photo: "Select this photo",
    photo_selected: "Selected",
    click_to_inspect: "Tap a photo to see it full screen, then tap the checkmark to select it",
    save_pct: "Save {pct}%",
    bonus_pct: "{pct}% Bonus",
    early_access: "Early access slots",
    early_access_hint: "A number you control. It shows on the landing hero and on every plan card, and disappears completely once it reaches zero.",
    slots_left: "Slots left",
    slots_total: "Slots in total",
    slot_taken: "One slot taken",
    early_access_saved: "Early access updated",
    slots_left_badge: "Only {left} of {total} early-access slots left",
    hero_early_access: "Exclusive Early Access: {left} of {total} Studios remaining",
    normally: "Normally {price}",
    lifetime_access: "Lifetime access — no expiry date",
    original_price: "Normal price",
    whatsapp: "WhatsApp",
    client_phone: "Client WhatsApp number",
    studio_whatsapp: "Studio WhatsApp number",
    send_whatsapp: "Send WhatsApp update",
    send_invite_whatsapp: "Send gallery link on WhatsApp",
    no_phone: "Add the client's WhatsApp number first",
    extra_price_override: "Extra edit price for this project (leave blank for studio default)",
    demo_eyebrow: "See it in motion",
    demo_title: "Your client picks their photos in under two minutes.",
    demo_body:
      "This is the real interface. Tap any photo to open it full screen, swipe between shots, pinch to inspect the details — then notes, prints and frames follow in guided steps. Nothing to install, nothing to explain.",
    demo_try: "Tap the photos to try it",
    demo_f1: "Notes on every chosen photo, straight to your retoucher",
    demo_f2: "Print sizes and frame codes captured exactly",
    demo_f3: "A priced summary and invoice waiting in your dashboard",
    trial_note: "7 days free · No card required · Cancel anytime",
    upload_logo: "Upload logo",
    uploading: "Uploading…",
    logo_hint: "PNG, JPG or WEBP · max 500 KB. Your logo replaces Seleksa on the client gallery, summary and PDF.",
    or_paste_url: "or paste an image URL",
    powered_by: "Powered by Seleksa",
    powered_by_body: "This gallery runs on Seleksa — the photo selection tool for studios.",
    trial_upsell_title: "Your studio is on a free trial",
    trial_upsell_body: "Upgrade before the trial ends to remove the Seleksa mark, unlock unlimited galleries and keep your analytics history.",
    see_plans: "See plans",
    later: "Maybe later",
    trial_limit_reached: "Free trials allow 2 active galleries. Upgrade to create more.",
    add_another_size: "Add another size",
    print_row: "Print",
    remove_print: "Remove this print",
    prints_multi_hint: "Tag each photo with the exact size you want. Add several sizes to the same photo if you need more than one print.",
    trial_days_left: "days left in trial",
    days: "days",
    trial_countdown_eyebrow: "Free trial",
    trial_countdown_title: "Your trial is running",
    trial_countdown_body: "When the trial ends your galleries stay online, but you'll need a plan to create or edit them.",
    oto_eyebrow: "One-time offer · 24 hours",
    oto_body: "Pay the annual plan within the next 24 hours and we'll add {months} extra months free — 18 months for the price of 12.",
    best_value: "Best value",
    bonus_months_free: "months free",
    owner_only: "Owner only",
    bank_transfer: "Bank transfer",
    renews_on: "Renews on",
    sorter_phase1_title: "Auto-Sorter · Phase 1 (before editing)",
    sorter_phase1_body: "Downloads a script you run inside the original event folder. It finds this client's chosen frames (RAW, CR3 and XMP together), copies them into Selected_<code>_<client>/EDIT, and puts the ordered prints in /CETAK renamed with their print size and frame code.",
    sorter_phase2_title: "Magic Print Sorter · Phase 2 (after editing)",
    sorter_phase2_body: "Run this once in the folder holding all your finished projects. It scans every subfolder, reads the print tags in the filenames, and copies each finished export into MASTER_SIAP_CETAK sorted by print size.",
    download_windows: "Download for Windows (.bat)",
    download_mac: "Download for Mac (.sh)",
    mac_permission_hint: "On macOS you may need to allow the script first: open Terminal, run chmod +x on the file, then run it from the folder.",
    sorter_failed: "Could not build the script",
    add_extra: "Add as extra edit",
    preparing_gallery: "Preparing your gallery",
    photos_cached: "photos ready",
    preloader_hint: "We're loading every preview first so browsing and zooming feel instant.",
    folders: "Folders",
    all_galleries: "All galleries",
    unfiled: "Unfiled",
    new_folder: "New folder",
    rename_folder: "Rename folder",
    folder_name: "Folder name",
    delete_folder: "Delete folder",
    selected_count: "{n} selected",
    move_to_folder: "Move to folder",
    delete_selected: "Delete selected",
    clear_selection: "Clear",
    batch_moved: "Galleries moved",
    batch_deleted: "Galleries deleted",
    affiliates: "Affiliate marketers",
    affiliates_hint: "Enter a name to auto-generate a 6-character promo code, or type your own. The discount applies at checkout and every paid order is tracked here.",
    marketer_name: "Marketer name",
    promo_code: "Promo code",
    promo_code_optional: "Code (leave blank to auto-generate)",
    discount_percent: "Discount %",
    add_marketer: "Add marketer",
    conversions: "Paid orders",
    monthly_performance: "Monthly",
    have_promo_code: "Have a promo code?",
    apply_code: "Apply",
    promo_applied: "Code {code} applied · {pct}% off",
    promo_note: "Enter a marketer's code and the discount is applied to your payment automatically.",
    promo_price_after: "You pay {price} instead of {was}",
    sorter_eyebrow: "Automatic file sorting",
    sorter_title: "Your RAW files sort themselves into EDIT and CETAK.",
    sorter_body: "Download one script per gallery. Run it on your own drive and every chosen frame — RAW, CR3 and sidecar together — lands in the right folder, print orders renamed with size and frame code. Your photos never leave your computer.",
    sorter_raw: "Your drive · RAW folder",
    sorter_edit_folder: "Edit only",
    sorter_print_folder: "For print",
    sorter_phase2: "Phase 2 · Print master",
    sorter_showcase_phase2_body: "After retouching, the second script scans every folder and groups the finished exports by print size, ready to send to the lab.",
    folder_delete_note: "The galleries inside move back to Unfiled — nothing is deleted.",
    marketer_active: "Active",
    opened_not_submitted: "Opened, not submitted",
    never_opened: "Not opened yet",
    reminded: "Reminded",
    remind_client: "Send a reminder",
    reminder_sent: "Reminder emailed to your client",
    reminder_wa_only: "Opening WhatsApp with the reminder",
    reminder_failed: "Could not send the reminder",
    sorter_preview_title: "Preview the folder structure",
    sorter_preview_empty: "Nothing in this folder yet",
    filter_all: "All",
    filter_locked: "Locked out",
    filter_expiring: "Expiring ≤7 days",
    filter_lifetime: "Lifetime",
    commission_percent: "Commission %",
    commission_label: "Commission",
    earned: "Earned",
    paid_out: "Paid out",
    outstanding: "Outstanding",
    mark_paid: "Mark as paid",
    payout_amount: "Payout amount (IDR)",
    payout_recorded: "Payout recorded",
    payout_history: "Payout history",
    no_payouts: "No payouts recorded yet",
    delete_payout: "Delete this payout",    marketer_inactive: "Inactive",
    deactivate: "Deactivate",
    activate: "Activate",
    add_extra_full: "Add as extra edit · +{price}",
    subscription_override: "Subscription override",
    set_subscription: "Set subscription",
    override_hint: "Switch the plan, inject days or lock a studio out immediately. Days are added on top of the current expiry unless you count from today.",
    days_to_add: "Days to add",
    count_from_today: "Count from today",
    make_lifetime: "Grant lifetime",
    lock_now: "Lock out now",
    apply_override: "Apply",
    override_saved: "Subscription updated",
    no_plan_change: "Keep current plan",
    new_expiry: "New expiry",
    search_projects: "Search by name, code or client",
    sort_newest: "Newest first",
    sort_alpha: "Name A–Z",
    no_search_results: "No galleries match that search.",
    delete_project: "Delete gallery",
    delete_warning: "This permanently removes the gallery, the client's selection, notes and invoice. Type HAPUS to confirm.",
    delete_paid_only: "Deleting galleries is available on a paid plan.",
    project_deleted: "Gallery deleted",
    project_module: "Gallery type",
    module_edit_print: "Edits + printing",
    module_edit_only: "Edits only",
    module_hint: "Edits only hides the print step from the client and leaves prints off the summary, PDF and invoice.",
    templates_hint: "Save your photography packages here. On the New Gallery screen you can apply one with a single click.",
    no_templates_settings: "No templates yet — create them under Settings.",
    add_template: "Add template",
    accounts: "Accounts",
    recent_logins: "Recent logins",
    recent_logins_hint: "The last 10 studio and staff sign-ins, kept for 30 days. Client gallery sign-ins are not logged.",
    recent_logins_empty: "No sign-ins recorded yet.",
    login_failed: "Failed",
    accounts_hint: "Every registered account on Seleksa. Deleting an account signs it out everywhere.",
    search_accounts: "Search by email or name",
    delete_account: "Delete account",
    delete_account_warning: "This permanently removes the account and signs it out everywhere.",
    delete_studio: "Delete studio",
    delete_studio_warning: "Deletes the studio with all of its accounts, galleries and payment records.",
    sub_status_trialing: "Free trial",
    sub_status_active: "Active",
    locked_out: "Locked out",
    sub_status_lifetime: "Lifetime access",
    sub_status_expired: "Expired",
    step_of: "Step {n}/{total}",
    menu: "Menu",
    renewal_nudged: "Renewal nudged",
    subscription_status: "Subscription status",
    save_amount: "You save {amount}",
    per_month_equiv: "≈ {price} / month",
    trial_locked_title: "Your free trial has ended",
    trial_locked_body: "Choose a plan to unlock your dashboard again. Existing client galleries keep working.",
    templates: "Package templates",
    apply_template: "Apply",
    save_as_template: "Save these settings as a template",
    template_name: "Template name",
    template_saved: "Template saved",
    no_templates: "No templates yet — configure a gallery below and save it as one.",
    lp_s1_t: "Link the folder",
    lp_s1_b: "Paste a public Google Drive folder and set the project ID.",
    lp_s2_t: "Tap to select",
    lp_s2_b: "Clients open each photo full screen, zoom in and tick the ones they want.",
    lp_s3_t: "Prints & frames",
    lp_s3_b: "Required print quotas, frame codes and live extra-cost totals.",
    lp_s4_t: "Invoice & PDF",
    lp_s4_b: "Custom charges compiled into a branded summary and invoice.",
    lp_s5_t: "Tracked order",
    lp_s5_b: "Editing and printing milestones email the client automatically.",
    lp_s6_t: "Studio analytics",
    lp_s6_b: "Print size breakdowns and revenue, weekly to custom ranges.",
  },
  id: {
    brand_tag: "Seleksi dengan jelas",
    nav_login: "Masuk studio",
    nav_client: "Masuk klien",
    hero_eyebrow: "Untuk studio foto & fotografer lepas",
    hero_title_a: "Seleksi foto,",
    hero_title_b: "kini tanpa ribet.",
    hero_body:
      "Kirim satu tautan privat. Klien menggeser, memperbesar, memilih editan, cetakan dan pigura — Anda menerima ringkasan berharga, invoice, dan pesanan yang terlacak.",
    hero_cta: "Mulai uji coba gratis",
    how_it_runs: "Cara kerjanya",
    pricing: "Paket langganan",
    pricing_body: "Mulai dengan uji coba 7 hari. Bayar dengan QRIS, virtual account, e-wallet atau transfer bank.",
    per_month: "per bulan",
    per_year: "per tahun",
    choose_plan: "Pilih paket",
    footer_note: "Seleksi klien, terkurasi.",
    nav_about: "Tentang kami",
    nav_contact: "Kontak",
    nav_privacy: "Kebijakan privasi",
    nav_terms: "Syarat layanan",
    back_home: "Kembali ke beranda",
    last_updated: "Terakhir diperbarui",
    contact_title: "Bicara langsung dengan tim kami di Medan.",
    contact_intro:
      "Pertanyaan soal harga, galeri yang bermasalah, atau ide kerja sama — kirimkan saja. Setiap pesan kami baca sendiri.",
    support_email: "Email dukungan",
    email_note: "Paling cocok untuk pertanyaan detail, catatan tagihan, dan hal yang perlu tangkapan layar.",
    whatsapp: "WhatsApp",
    whatsapp_note: "Paling cepat saat hari pemotretan. Kirim pesan, kami balas begitu selesai di lokasi.",
    hq_address: "Kantor pusat",
    address_note: "Kunjungan dengan perjanjian — kabari dulu agar ada yang menyambut Anda.",
    office_hours: "Jam kerja",
    hours_value: "SEN–SAB · 09.00–18.00 WIB",
    hours_note: "Email di luar jam ini kami jawab pagi hari kerja berikutnya.",
    send_message: "Kirim pesan",
    form_heading: "Tulis langsung ke kami",
    subject: "Topik",
    message: "Pesan",
    message_placeholder: "Ceritakan kebutuhan Anda. Semakin detail, semakin cepat kami bisa membantu.",
    subject_sales: "Penjualan & harga",
    subject_support: "Dukungan teknis",
    subject_billing: "Tagihan & pembayaran",
    subject_partnership: "Kerja sama / afiliasi",
    subject_other: "Lainnya",
    contact_sent: "Pesan terkirim",
    contact_sent_body: "Terima kasih — pesan Anda sudah kami terima. Biasanya kami balas dalam satu hari kerja.",
    contact_failed: "Pesan gagal terkirim. Silakan coba lagi.",
    form_privacy_note:
      "Data Anda hanya kami pakai untuk menjawab pertanyaan ini. Tidak ditambahkan ke daftar pemasaran.",
    err_name: "Mohon isi nama Anda (minimal 2 karakter).",
    err_email: "Mohon isi alamat email yang valid.",
    err_message: "Mohon tulis minimal 10 karakter agar kami bisa membantu dengan tepat.",
    print_sizes_hint: "Ukuran yang dicentang itulah pilihan klien Anda. Harga diambil dari katalog utama di Pengaturan.",
    no_catalog_sizes: "Belum ada ukuran cetak di katalog — tambahkan dulu di Pengaturan → Harga.",
    n_selected: "{n} dipilih",
    select_all: "Pilih semua",
    clear: "Kosongkan",
    package_price: "Harga paket",
    package_label: "Nama paket di invoice",
    package_label_placeholder: "Combo 7 Edit",
    package_price_hint: "Biaya paket yang disepakati. Otomatis masuk ke invoice proyek saat template ini dipakai.",
    discount_label: "Keterangan diskon",
    add_discount_line: "Tambah diskon lain",
    select_all_remaining: "Pilih semua (sesuai paket)",
    unselect_all: "Batalkan semua",
    bulk_selected: "{n} foto dipilih",
    print_qty: "Lembar",
    mandatory_prints_hint: "Klik ukuran dari daftar harga Anda untuk memasukkannya ke paket, lalu tentukan jumlah lembar yang sudah termasuk.",
    discount_amount: "Nilai diskon",
    discount_placeholder: "Diskon langganan",
    add_discount: "+ Tambah diskon",
    edit_discount: "Ubah diskon",
    apply_discount: "Terapkan diskon",
    discount_saved: "Diskon diterapkan",
    admin_override_hint: "Anda bisa ubah ukuran, kode pigura, tambah atau hapus cetakan kapan saja.",
    print_added: "Cetakan ditambahkan",
    print_removed: "Cetakan dihapus",
    remove_print: "Hapus cetakan",
    tsf_eyebrow: "Perjalanan klien",
    tsf_title: "Proses seleksi 3 langkah yang ringkas.",
    tsf_body:
      "Kami rampingkan perjalanan klien menjadi tiga layar. Lebih sedikit keputusan, tanpa bolak-balik, dan seleksi masuk dalam hitungan menit — bukan berhari-hari lewat WhatsApp.",
    tsf_step: "Langkah",
    tsf_s1_t: "Pilih editan",
    tsf_s1_b:
      "Satu grid, satu ketukan per foto. Kuota paket terhitung di depan mata, dan setiap tambahan langsung berharga sebelum dikonfirmasi.",
    tsf_s2_t: "Tambahkan catatan",
    tsf_s2_b:
      "Permintaan revisi menempel pada fotonya — cerahkan yang ini, hilangkan pantulan itu — jadi editor tak perlu menebak.",
    tsf_s3_t: "Atur cetakan",
    tsf_s3_b:
      "Kuota paket prabayar berkurang langsung. Saat satu ukuran habis, cetak tambahan terbuka dengan harga yang Anda atur.",

    login_title: "Selamat datang kembali.",
    signup_title: "Siapkan studio Anda.",
    studio_access: "Akses studio",
    new_studio: "Studio baru",
    continue_google: "Lanjut dengan Google",
    or_email: "atau email",
    studio_name: "Nama studio",
    your_name: "Nama Anda",
    email: "Email",
    password: "Kata sandi",
    enter_studio: "Masuk studio",
    create_studio: "Buat studio",
    to_signup: "Belum punya akun? Buat studio Anda",
    to_login: "Sudah punya studio? Masuk",
    client_login_title: "Lacak & pilih.",
    access_code: "Kode akses",
    client_signin: "Buka galeri saya",
    client_login_hint: "Gunakan email dan kode akses dari fotografer Anda.",

    dashboard: "Dasbor",
    projects: "Proyek",
    new_project: "Proyek baru",
    analytics: "Analitik",
    settings: "Pengaturan",
    billing: "Tagihan",
    platform: "Platform",
    logout: "Keluar",
    no_projects: "Belum ada galeri",
    no_projects_body: "Buat proyek pertama Anda dan bagikan tautan privat ke klien.",
    photos: "Foto",
    edits: "Editan",
    prints: "Cetak",
    extra: "Tambahan",
    manage: "Kelola",
    awaiting_client: "Menunggu klien",
    submitted: "Terkirim",
    project_code: "ID Proyek",
    project_title: "Judul proyek",
    client_name: "Nama klien",
    client_email: "Email klien",
    drive_link: "Tautan folder Google Drive (dibagikan publik)",
    demo_photos: "Gunakan foto demo (untuk uji alur)",
    edit_quota: "Jumlah foto yang termasuk paket edit",
    allow_extra: "Izinkan permintaan edit tambahan",
    max_extra: "Maksimal edit tambahan",
    print_quotas: "Cetak wajib (ukuran × jumlah)",
    add_size: "Tambah ukuran",
    print_sizes: "Ukuran cetak yang ditawarkan",
    create_gallery: "Buat galeri",
    save_changes: "Simpan perubahan",
    client_link: "Tautan privat klien",
    copy: "Salin",
    preview: "Pratinjau",
    resync: "Sinkron ulang folder Drive",
    download_pdf: "Unduh PDF",
    email_summary: "Kirim ringkasan",
    reopen: "Buka kembali untuk klien",
    order_status: "Status pesanan",
    custom_charges: "Biaya kustom & tambahan",
    charge_label: "Keterangan",
    amount: "Jumlah (IDR)",
    add_charge: "Tambah biaya",
    invoice: "Invoice",
    total: "Total",
    client_selection: "Pilihan klien",
    access_code_label: "Kode akses klien",
    reset_code: "Reset kode",

    step_select: "Pilih",
    step_notes: "Catatan",
    notes_title: "Ada yang perlu diubah?",
    notes_body:
      "Tempelkan catatan pada foto mana pun — cerahkan, potong lebih rapat, hilangkan pantulan. Editor Anda melihat catatan itu tepat pada fotonya.",
    general_note_placeholder: "Ada catatan untuk keseluruhan pesanan?",
    quota_reached_toast: "Kuota paket {n} foto sudah penuh. Pilihan berikutnya dikenakan {price} per foto.",
    extra_added_toast: "Edit tambahan ditambahkan · {price}",
    quota_full_no_extra: "Paket Anda mencakup {n} foto dan edit tambahan tidak diaktifkan.",
    extra_cap_reached: "Maksimal {n} edit tambahan.",
    quota_left: "sisa {n} dari paket Anda",
    quota_used_up: "Kuota paket sudah penuh",
    then_each: "selanjutnya {price} per foto",
    max_total: "maks {n} total",
    n_left: "sisa {n}",
    quota_spent: "sudah terpakai",
    price_on_request: "harga menyusul dari studio",
    package_presets_hint: "Ketuk harga yang sering Anda pakai, atau tulis sendiri. Harga ini muncul sebagai baris paket di invoice.",
    unpriced_sizes_title: "Ukuran ini belum punya harga di katalog Anda:",
    unpriced_sizes_body:
      "Klien tetap bisa memintanya, tetapi cetak tambahan ukuran ini tidak akan otomatis ditagih. Tambahkan di Pengaturan → Harga, atau hapus centangnya di sini.",
    step_review: "Tinjau",
    step_extra: "Edit tambahan",
    step_prints: "Cetak",
    step_confirm: "Konfirmasi",
    choose_edits: "Pilih foto Anda",
    choose_edits_body: "Pilih {n} foto untuk diedit. Ketuk foto untuk membukanya layar penuh, lalu ketuk untuk memilih.",
    gallery_preparing: "Fotografer Anda masih menyiapkan galeri ini.",
    note_placeholder: "Cerahkan bayangan, hapus papan di sebelah kiri...",
    avg_per_project: "Rata-rata / proyek",
    review_notes: "Tambahkan catatan",
    review_notes_body: "Tinggalkan instruksi untuk editor pada setiap foto terpilih.",
    extra_title: "Butuh edit tambahan?",
    extra_body: "Tambahkan foto di luar paket seharga {price} per foto.",
    prints_title: "Cetak & pigura",
    prints_body: "Pilih ukuran dan kode pigura. Cetak wajib sudah termasuk paket.",
    confirm_title: "Tinjauan akhir",
    confirm_body: "Periksa semuanya, tambahkan catatan terakhir, lalu kirim.",
    selected: "terpilih",
    included: "Termasuk",
    extra_charge: "Tambahan",
    running_total: "Perkiraan biaya tambahan",
    frame_code: "Kode pigura",
    notes: "Catatan",
    add_print: "Tambah cetak",
    printing: "Dicetak",
    save: "Simpan",
    continue: "Lanjut",
    back: "Kembali",
    submit_selection: "Kirim pilihan",
    submitted_ok: "Pilihan terkirim",
    general_note: "Ada lagi untuk fotografer Anda?",
    zoom_hint: "Ketuk foto untuk memperbesar",
    skip: "Lewati",
    keep: "Simpan",
    undo: "Batal",
    deck_done: "Semua foto sudah Anda lihat.",

    summary: "Ringkasan pilihan",
    tracking: "Pelacakan pesanan",
    milestone_selection_received: "Pilihan diterima",
    milestone_editing_complete: "Editing selesai",
    milestone_printing_complete: "Pencetakan selesai",
    milestone_delivered: "Terkirim ke klien",
    print_page: "Cetak halaman ini",
    my_projects: "Galeri saya",
    open_gallery: "Buka galeri",
    view_summary: "Lihat ringkasan",

    branding: "Branding studio",
    logo_url: "URL logo",
    pricing_defaults: "Harga bawaan",
    extra_edit_price: "Harga per foto edit tambahan",
    frame_price: "Harga per pigura",
    price_per_size: "Harga per ukuran cetak",
    staff: "Akun staf",
    add_staff: "Tambah staf",
    role: "Peran",
    remove: "Hapus",
    cancel: "Batal",
    edit: "Ubah",
    revenue: "Pendapatan",
    revenue_hidden: "Data keuangan hanya untuk Owner",
    period_week: "Minggu ini",
    period_month: "Bulan ini",
    period_year: "Tahun ini",
    period_custom: "Rentang kustom",
    digital_only: "Edit digital saja",
    for_print: "Edit untuk cetak",
    frames: "Pigura",
    pipeline: "Alur kerja",
    plans: "Paket langganan",
    tenants: "Studio",
    plan_name: "Nama paket",
    price: "Harga (IDR)",
    price_usd: "Harga (USD)",
    pay_with: "Bayar dengan",
    pay_local: "Indonesia · QRIS, VA, e-wallet",
    pay_paypal: "Internasional · PayPal (USD)",
    paypal_note: "Untuk studio di luar Indonesia. Ditagih sekali dalam USD — tanpa penagihan berulang otomatis.",
    paypal_no_usd_price: "Harga USD untuk paket ini belum diatur.",
    paypal_not_confirmed: "PayPal tidak mengonfirmasi pembayaran ini",
    paypal_pending_review: "PayPal sedang meninjau pembayaran ini",
    paypal_pending_review_hint: "PayPal sudah menerima pembayaran tetapi masih menahannya untuk ditinjau — biasanya beberapa jam. Paket Anda akan aktif sendiri begitu dana cair, dan halaman ini akan menampilkannya. Tidak ada yang perlu Anda lakukan lagi.",
    order_status_pending_review: "Ditinjau",
    order_status_expired: "Kedaluwarsa",
    order_status_pending: "Menunggu pembayaran",
    order_status_declined: "Ditolak",
    order_status_refunded: "Dikembalikan",
    order_status_reversed: "Dibatalkan",
    order_status_paid: "Lunas",
    payment_received: "Pembayaran diterima",
    payment_failed: "Pembayaran gagal",
    checkout_closed: "Checkout ditutup",
    interval_months: "Interval (bulan)",
    save_plan: "Simpan paket",
    current_plan: "Paket saat ini",
    trial_until: "Uji coba sampai",
    pay_now: "Bayar sekarang",
    payment_history: "Riwayat pembayaran",
    apply: "Terapkan rentang",
    saved_ok: "Perubahan tersimpan",
    support: "Dukungan Seleksa",
    support_body: "Ada pertanyaan soal akun studio, tagihan atau proyek? Chat tim kami di WhatsApp.",
    chat_whatsapp: "Chat di WhatsApp",
    support_prefill: "Halo dukungan Seleksa, saya butuh bantuan soal ",
    ob_eyebrow: "Cara memilih",
    ob_title: "Cukup tiga gerakan",
    ob_tap_open: "Ketuk foto di grid untuk membukanya layar penuh.",
    ob_swipe: "Geser ke kiri atau kanan (atau pakai panah) untuk berpindah foto.",
    ob_tap_select: "Ketuk foto — atau tanda centang — untuk memilih atau membatalkan.",
    ob_pinch: "Cubit dengan dua jari untuk memperbesar detail.",
    ob_start: "Mulai memilih",
    close: "Tutup",
    select_photo: "Pilih foto ini",
    photo_selected: "Terpilih",
    click_to_inspect: "Ketuk foto untuk melihatnya layar penuh, lalu ketuk tanda centang untuk memilih",
    save_pct: "Hemat {pct}%",
    bonus_pct: "Bonus {pct}%",
    early_access: "Slot early access",
    early_access_hint: "Angka ini Anda tentukan sendiri. Muncul di hero halaman depan dan di setiap kartu paket, dan hilang sepenuhnya saat mencapai nol.",
    slots_left: "Sisa slot",
    slots_total: "Total slot",
    slot_taken: "Satu slot terpakai",
    early_access_saved: "Early access diperbarui",
    slots_left_badge: "Sisa {left} dari {total} slot early access",
    hero_early_access: "Early Access Eksklusif: sisa {left} dari {total} studio",
    normally: "Harga normal {price}",
    lifetime_access: "Akses seumur hidup — tanpa tanggal kedaluwarsa",
    original_price: "Harga normal",
    whatsapp: "WhatsApp",
    client_phone: "Nomor WhatsApp klien",
    studio_whatsapp: "Nomor WhatsApp studio",
    send_whatsapp: "Kirim update WhatsApp",
    send_invite_whatsapp: "Kirim tautan galeri via WhatsApp",
    no_phone: "Tambahkan nomor WhatsApp klien dahulu",
    extra_price_override: "Harga edit tambahan untuk proyek ini (kosongkan untuk harga bawaan studio)",
    demo_eyebrow: "Lihat langsung",
    demo_title: "Klien Anda memilih fotonya dalam kurang dari dua menit.",
    demo_body:
      "Ini antarmuka sesungguhnya. Ketuk foto untuk membukanya layar penuh, geser antar foto, cubit untuk memeriksa detail — lalu catatan, cetakan dan pigura mengikuti langkah demi langkah. Tanpa instalasi, tanpa penjelasan panjang.",
    demo_try: "Ketuk fotonya untuk mencoba",
    demo_f1: "Catatan di setiap foto pilihan, langsung ke editor Anda",
    demo_f2: "Ukuran cetak dan kode pigura tercatat persis",
    demo_f3: "Ringkasan berharga dan invoice siap di dasbor Anda",
    trial_note: "7 hari gratis · Tanpa kartu kredit · Batalkan kapan saja",
    upload_logo: "Unggah logo",
    uploading: "Mengunggah…",
    logo_hint: "PNG, JPG atau WEBP · maks 500 KB. Logo Anda menggantikan Seleksa di galeri klien, ringkasan dan PDF.",
    or_paste_url: "atau tempel URL gambar",
    powered_by: "Didukung oleh Seleksa",
    powered_by_body: "Galeri ini berjalan di Seleksa — alat seleksi foto untuk studio.",
    trial_upsell_title: "Studio Anda masih dalam uji coba gratis",
    trial_upsell_body: "Tingkatkan sebelum uji coba berakhir untuk menghapus tanda Seleksa, membuka galeri tanpa batas dan menyimpan riwayat analitik.",
    see_plans: "Lihat paket",
    later: "Nanti saja",
    trial_limit_reached: "Uji coba gratis hanya untuk 2 galeri aktif. Tingkatkan untuk membuat lebih banyak.",
    add_another_size: "Tambah ukuran lain",
    print_row: "Cetak",
    remove_print: "Hapus cetakan ini",
    prints_multi_hint: "Tandai setiap foto dengan ukuran yang Anda inginkan. Tambahkan beberapa ukuran pada foto yang sama bila perlu lebih dari satu cetakan.",
    trial_days_left: "hari uji coba tersisa",
    days: "hari",
    trial_countdown_eyebrow: "Uji coba gratis",
    trial_countdown_title: "Uji coba Anda sedang berjalan",
    trial_countdown_body: "Saat uji coba berakhir, galeri Anda tetap online, tetapi Anda perlu paket untuk membuat atau mengubahnya.",
    oto_eyebrow: "Penawaran sekali saja · 24 jam",
    oto_body: "Bayar paket tahunan dalam 24 jam ke depan dan kami tambahkan {months} bulan gratis — 18 bulan dengan harga 12 bulan.",
    best_value: "Paling hemat",
    bonus_months_free: "bulan gratis",
    owner_only: "Hanya pemilik",
    bank_transfer: "Transfer bank",
    renews_on: "Diperbarui pada",
    sorter_phase1_title: "Auto-Sorter · Tahap 1 (sebelum editing)",
    sorter_phase1_body: "Mengunduh skrip yang Anda jalankan di dalam folder acara asli. Skrip mencari foto pilihan klien ini (RAW, CR3 dan XMP sekaligus), menyalinnya ke Selected_<kode>_<klien>/EDIT, dan menaruh pesanan cetak di /CETAK dengan nama file yang sudah diberi ukuran cetak serta kode pigura.",
    sorter_phase2_title: "Magic Print Sorter · Tahap 2 (setelah editing)",
    sorter_phase2_body: "Jalankan sekali di folder yang memuat semua proyek jadi. Skrip memindai seluruh subfolder, membaca tanda ukuran cetak pada nama file, lalu menyalin setiap hasil akhir ke MASTER_SIAP_CETAK yang tersusun per ukuran cetak.",
    download_windows: "Unduh untuk Windows (.bat)",
    download_mac: "Unduh untuk Mac (.sh)",
    mac_permission_hint: "Di macOS Anda mungkin perlu mengizinkan skrip dulu: buka Terminal, jalankan chmod +x pada file tersebut, lalu jalankan dari folder itu.",
    sorter_failed: "Skrip gagal dibuat",
    add_extra: "Tambah sebagai editan ekstra",
    preparing_gallery: "Menyiapkan galeri Anda",
    photos_cached: "foto siap",
    preloader_hint: "Kami memuat semua pratinjau lebih dulu agar menjelajah dan memperbesar terasa instan.",
    folders: "Folder",
    all_galleries: "Semua galeri",
    unfiled: "Belum difolderkan",
    new_folder: "Folder baru",
    rename_folder: "Ubah nama folder",
    folder_name: "Nama folder",
    delete_folder: "Hapus folder",
    selected_count: "{n} dipilih",
    move_to_folder: "Pindahkan ke folder",
    delete_selected: "Hapus yang dipilih",
    clear_selection: "Batalkan",
    batch_moved: "Galeri dipindahkan",
    batch_deleted: "Galeri dihapus",
    affiliates: "Marketer afiliasi",
    affiliates_hint: "Masukkan nama untuk membuat kode promo 6 karakter secara otomatis, atau tulis kode Anda sendiri. Diskon berlaku saat checkout dan setiap pesanan berbayar tercatat di sini.",
    marketer_name: "Nama marketer",
    promo_code: "Kode promo",
    promo_code_optional: "Kode (biarkan kosong untuk otomatis)",
    discount_percent: "Diskon %",
    add_marketer: "Tambah marketer",
    conversions: "Pesanan berbayar",
    monthly_performance: "Bulanan",
    have_promo_code: "Punya kode promo?",
    apply_code: "Terapkan",
    promo_applied: "Kode {code} aktif · diskon {pct}%",
    promo_note: "Masukkan kode marketer dan diskon otomatis diterapkan pada pembayaran Anda.",
    promo_price_after: "Anda bayar {price} dari {was}",
    sorter_eyebrow: "Penyortiran file otomatis",
    sorter_title: "File RAW Anda menyortir dirinya ke folder EDIT dan CETAK.",
    sorter_body: "Unduh satu skrip per galeri. Jalankan di drive Anda sendiri dan setiap foto pilihan klien — RAW, CR3 beserta sidecar — masuk ke folder yang tepat, pesanan cetak langsung diberi nama ukuran dan kode frame. Foto Anda tidak pernah keluar dari komputer.",
    sorter_raw: "Drive Anda · folder RAW",
    sorter_edit_folder: "Hanya edit",
    sorter_print_folder: "Untuk cetak",
    sorter_phase2: "Fase 2 · Master cetak",
    sorter_showcase_phase2_body: "Setelah retouch, skrip kedua memindai semua folder dan mengelompokkan hasil akhir per ukuran cetak, siap dikirim ke lab.",
    folder_delete_note: "Galeri di dalamnya kembali ke Belum difolderkan — tidak ada yang dihapus.",
    marketer_active: "Aktif",
    opened_not_submitted: "Dibuka, belum dikirim",
    never_opened: "Belum dibuka",
    reminded: "Diingatkan",
    remind_client: "Kirim pengingat",
    reminder_sent: "Pengingat dikirim ke email klien",
    reminder_wa_only: "Membuka WhatsApp dengan pesan pengingat",
    reminder_failed: "Pengingat gagal dikirim",
    sorter_preview_title: "Lihat struktur folder",
    sorter_preview_empty: "Belum ada file di folder ini",
    filter_all: "Semua",
    filter_locked: "Terkunci",
    filter_expiring: "Berakhir ≤7 hari",
    filter_lifetime: "Seumur hidup",
    commission_percent: "Komisi %",
    commission_label: "Komisi",
    earned: "Diperoleh",
    paid_out: "Sudah dibayar",
    outstanding: "Belum dibayar",
    mark_paid: "Tandai lunas",
    payout_amount: "Nominal pembayaran (IDR)",
    payout_recorded: "Pembayaran dicatat",
    payout_history: "Riwayat pembayaran",
    no_payouts: "Belum ada pembayaran",
    delete_payout: "Hapus pembayaran ini",
    marketer_inactive: "Nonaktif",
    deactivate: "Nonaktifkan",
    activate: "Aktifkan",
    add_extra_full: "Tambah sebagai editan ekstra · +{price}",
    subscription_override: "Atur langganan",
    set_subscription: "Simpan langganan",
    override_hint: "Ganti paket, tambah hari, atau kunci studio seketika. Hari ditambahkan di atas masa aktif saat ini kecuali Anda menghitung dari hari ini.",
    days_to_add: "Tambah hari",
    count_from_today: "Hitung dari hari ini",
    make_lifetime: "Beri akses seumur hidup",
    lock_now: "Kunci sekarang",
    apply_override: "Terapkan",
    override_saved: "Langganan diperbarui",
    no_plan_change: "Pertahankan paket saat ini",
    new_expiry: "Masa aktif baru",
    search_projects: "Cari nama, kode atau klien",
    sort_newest: "Terbaru dulu",
    sort_alpha: "Nama A–Z",
    no_search_results: "Tidak ada galeri yang cocok dengan pencarian itu.",
    delete_project: "Hapus galeri",
    delete_warning: "Ini menghapus permanen galeri, pilihan klien, catatan dan invoice-nya. Ketik HAPUS untuk konfirmasi.",
    delete_paid_only: "Menghapus galeri hanya tersedia pada paket berbayar.",
    project_deleted: "Galeri dihapus",
    project_module: "Jenis galeri",
    module_edit_print: "Edit + cetak",
    module_edit_only: "Hanya edit",
    module_hint: "Mode hanya edit menyembunyikan langkah cetak dari klien dan menghilangkan cetakan dari ringkasan, PDF dan invoice.",
    templates_hint: "Simpan paket fotografi Anda di sini. Di layar Galeri Baru, Anda bisa menerapkannya dengan satu klik.",
    no_templates_settings: "Belum ada template — buat dulu di menu Pengaturan.",
    add_template: "Tambah template",
    accounts: "Akun",
    recent_logins: "Login terbaru",
    recent_logins_hint: "10 login studio dan staf terakhir, disimpan 30 hari. Login galeri klien tidak dicatat.",
    recent_logins_empty: "Belum ada login yang tercatat.",
    login_failed: "Gagal",
    accounts_hint: "Semua akun terdaftar di Seleksa. Menghapus akun akan mengeluarkannya dari semua perangkat.",
    search_accounts: "Cari email atau nama",
    delete_account: "Hapus akun",
    delete_account_warning: "Ini menghapus akun secara permanen dan mengeluarkannya dari semua perangkat.",
    delete_studio: "Hapus studio",
    delete_studio_warning: "Menghapus studio beserta semua akun, galeri dan catatan pembayarannya.",
    sub_status_trialing: "Uji coba gratis",
    sub_status_active: "Aktif",
    locked_out: "Terkunci",
    sub_status_lifetime: "Akses seumur hidup",
    sub_status_expired: "Kedaluwarsa",
    step_of: "Langkah {n}/{total}",
    menu: "Menu",
    renewal_nudged: "Diingatkan perpanjangan",
    subscription_status: "Status langganan",
    save_amount: "Anda hemat {amount}",
    per_month_equiv: "≈ {price} / bulan",
    trial_locked_title: "Uji coba gratis Anda telah berakhir",
    trial_locked_body: "Pilih paket untuk membuka kembali dasbor Anda. Galeri klien yang sudah ada tetap berjalan.",
    templates: "Template paket",
    apply_template: "Terapkan",
    save_as_template: "Simpan pengaturan ini sebagai template",
    template_name: "Nama template",
    template_saved: "Template tersimpan",
    no_templates: "Belum ada template — atur galeri di bawah lalu simpan sebagai template.",
    lp_s1_t: "Hubungkan folder",
    lp_s1_b: "Tempel tautan folder Google Drive publik dan tentukan ID proyek.",
    lp_s2_t: "Ketuk untuk memilih",
    lp_s2_b: "Klien membuka setiap foto layar penuh, memperbesar, lalu mencentang pilihannya.",
    lp_s3_t: "Cetak & pigura",
    lp_s3_b: "Kuota cetak wajib, kode pigura, dan total biaya tambahan secara langsung.",
    lp_s4_t: "Invoice & PDF",
    lp_s4_b: "Biaya kustom terangkum dalam ringkasan dan invoice berlogo studio Anda.",
    lp_s5_t: "Pesanan terlacak",
    lp_s5_b: "Tahap editing dan pencetakan otomatis mengirim email ke klien.",
    lp_s6_t: "Analitik studio",
    lp_s6_b: "Rincian ukuran cetak dan pendapatan, dari mingguan hingga rentang kustom.",
  },
};

const I18nContext = createContext(null);

export const I18nProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("seleksa_lang") || "id");

  useEffect(() => {
    localStorage.setItem("seleksa_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback((key, vars) => {
    let str = DICT[lang]?.[key] ?? DICT.en[key] ?? key;
    if (vars) Object.entries(vars).forEach(([k, v]) => (str = str.replace(`{${k}}`, v)));
    return str;
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);
